import { fotoDeHerramienta } from '@/lib/supabase/deposito'
import type { Herramienta } from '@/lib/supabase/tipos-bd'
import { Pastilla } from '@/shared/ui/Pastilla/Pastilla'
import { CeldaDoble, type ColumnaTabla } from '@/shared/ui/Tabla/Tabla'

import { Link } from 'react-router-dom'

import { useSesion } from '@/modules/auth/useSesion'

import { PaginaMaestro } from '../compartido/PaginaMaestro'
import type { CampoMaestro, RegistroMaestro } from '../compartido/tipos'
import { HerramientasEnCalle } from './HerramientasEnCalle'
import { TrasladosPendientes } from './TrasladosPendientes'
import estilos from './PaginaHerramientas.module.css'

type FilaHerramienta = Herramienta & RegistroMaestro

const COLUMNAS: ColumnaTabla<FilaHerramienta>[] = [
  {
    // La foto delante del nombre, pequeña. Quien busca una herramienta en el
    // estante la reconoce antes por su forma que por «DOB-02»; y las tres
    // dobladoras, que se llaman casi igual, dejan de confundirse.
    encabezado: '',
    celda: (herramienta) => (
      <div className={estilos.conFoto}>
        {fotoDeHerramienta(herramienta.foto) ? (
          <img
            className={estilos.miniatura}
            src={fotoDeHerramienta(herramienta.foto)!}
            alt=""
            loading="lazy"
          />
        ) : (
          <span className={estilos.sinFoto} aria-hidden="true" />
        )}
        <CeldaDoble principal={herramienta.nombre} secundario={herramienta.codigo} />
      </div>
    ),
  },
  {
    encabezado: 'En bodega',
    ancho: '130px',
    derecha: true,
    celda: (herramienta) => (
      <span className="cifra">
        {herramienta.cantidad_disponible} / {herramienta.cantidad_total}
      </span>
    ),
  },
  {
    encabezado: 'En la calle',
    ancho: '130px',
    celda: (herramienta) => {
      const enCalle = herramienta.cantidad_total - herramienta.cantidad_disponible

      if (enCalle === 0) {
        return <Pastilla tono="neutro">Todo en bodega</Pastilla>
      }

      // «afuera» y no la unidad de medida en plural: pegarle una ese a
      // «unidad» daba «2 unidads», y hacerlo bien exigiría conjugar cada
      // unidad que MEGA escriba —unidad/unidades, par/pares, juego/juegos—.
      // La cifra ya dice cuántas y la columna ya dice de qué se habla.
      return <Pastilla tono="enCurso">{`${enCalle} afuera`}</Pastilla>
    },
  },
  {
    encabezado: 'Foto al devolver',
    ancho: '150px',
    celda: (herramienta) => (herramienta.exige_foto_devolucion ? 'Exigida' : 'No exigida'),
  },
]

/**
 * Inventario de herramientas.
 *
 * DOS PANTALLAS EN UNA, Y EN ESTE ORDEN. Arriba, qué hay afuera y con quién:
 * es la pregunta que trae a alguien aquí. Nadie abre esto a preguntarse
 * cuántas escaleras compró la empresa —eso se sabe—; se abre a buscar la que
 * no aparece. Abajo, el catálogo, que se consulta de vez en cuando.
 *
 * La cantidad disponible NO se edita a mano desde aquí: la mueven la
 * programación —que aparta al asignar— y la bodega —que la libera al
 * recibir—. Un inventario que se puede cuadrar a mano deja de ser un
 * inventario y pasa a ser una opinión.
 */
export function PaginaHerramientas() {
  const { puede } = useSesion()

  const campos: CampoMaestro[] = [
    { nombre: 'codigo', etiqueta: 'Código', tipo: 'texto', obligatorio: true, medio: true },
    { nombre: 'nombre', etiqueta: 'Nombre', tipo: 'texto', obligatorio: true, medio: true },
    {
      nombre: 'cantidad_total',
      etiqueta: 'Cantidad total',
      tipo: 'numero',
      obligatorio: true,
      medio: true,
      minimo: 0,
      paso: '1',
      ayuda: 'Cuántas unidades tiene la empresa en total.',
    },
    { nombre: 'unidad_medida', etiqueta: 'Unidad de medida', tipo: 'texto', medio: true },
    { nombre: 'descripcion', etiqueta: 'Descripción', tipo: 'area' },
  ]

  return (
    <>
      <TrasladosPendientes />
      <HerramientasEnCalle />

      <PaginaMaestro<FilaHerramienta>
        titulo="Inventario de herramientas"
        subtitulo="El catálogo completo. Se aparta al programar y vuelve cuando bodega la recibe."
        tabla="herramientas"
        ordenarPor="nombre"
        singular="herramienta"
        columnas={COLUMNAS}
        campos={campos}
        // Junto al catálogo y no en el menú: se imprime una vez al empezar y
        // después solo cuando se rompe una etiqueta. Una entrada de menú
        // permanente para algo que se hace dos veces al año es ruido.
        mandoExtra={
          puede('herramientas', 'editar') ? (
            <Link className={estilos.imprimir} to="/herramientas/etiquetas">
              Imprimir etiquetas QR
            </Link>
          ) : null
        }
        valoresNuevos={{ unidad_medida: 'unidad', cantidad_total: 1 }}
        textoDeBusqueda={(herramienta) => [herramienta.nombre, herramienta.codigo].join(' ')}
      />
    </>
  )
}
